import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { DATE_FORMAT, isOnSameDay } from '@shared/utils/DateUtils';
import { loadHospitalityBookingsForDate } from "@features/meeting-rooms/pages/models/hospitality.http";
import { HospitalityActionState, HospitalityMeetingBookingSummary } from "@features/meeting-rooms/pages/models/hospitality.models";
import { breakpoints, desktopMidWide, desktopSmall, desktopUberWide, desktopWide } from "@shared/ui/atoms/MediaQueries";
import { HospitalitySidebar } from "./HospitalitySidebar";
import { selectedOffice, setSidebarOpen } from "@features/meeting-rooms/pages/models/room-finder.duck";
import { selectActiveDay, setActiveDay } from '@features/whereabouts/store/editMovements.duck';
import Colours from "@shared/ui/atoms/Colours";
import { HospitalityBookingSummary } from "./HospitalityBookingSummary";
import Layout from "@shared/ui/organisms/layout/Layout";
import { PageTitle } from "@shared/ui/atoms/PageTitle";
import DateFlicker, { DateScale } from "@shared/ui/molecules/DateFlicker";
import { Moment } from "moment";
import moment from "moment";
import { LoadingSpinner } from "@shared/ui/atoms/LoadingSpinner";
import { Center } from "@shared/ui/atoms/StructuralLayout";
import { isInTeams } from '@shared/utils/TeamsUtils';
import IconButton from "@shared/ui/molecules/icon-button/IconButton";
import { IconTypes } from "@shared/ui/atoms/icon/Icon";
import { NoHospitalityBookings } from "./NoHospitalityBookings";
import { useTranslation } from "react-i18next";
import { HeadlineSmall } from "@shared/ui/atoms/fonts/Headline";

export function HospitalityOrdersPageView(props: Props) {
  document.body.style.backgroundColor = '#FAFAFA';
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const activeDate = useSelector(selectActiveDay);
  const office = useSelector(selectedOffice);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<HospitalityMeetingBookingSummary[]>([]);

  const inProgressBookings = useMemo(() => {
    return response.filter(r => r.status !== HospitalityActionState.Done);
  }, [response]);

  const doneBookings = useMemo(() => {
    return response.filter(r => r.status === HospitalityActionState.Done);
  }, [response]);

  useEffect(() => {
    if (activeDate && office) {
      setLoading(true);
      loadHospitalityBookingsForDate(office.id, activeDate.format(DATE_FORMAT))
        .then(setResponse)
        .finally(() => setLoading(false))
    }
  }, [activeDate, office]);

  const onDateChange = (date: Moment) => {
    dispatch(setActiveDay(date));
  }

  const dateLabel = useMemo(() => {
    const startOfThisWeek = moment().startOf('isoWeek')
    const startOfLastWeek = startOfThisWeek.clone().subtract(7, 'days');
    const startOfNextWeek = startOfThisWeek.clone().add(7, 'days');
    const startOfActiveDateWeek = activeDate?.clone().startOf('isoWeek');
    if (isOnSameDay(startOfThisWeek, startOfActiveDateWeek)) {
      return 'This week';
    } else if (isOnSameDay(startOfNextWeek, startOfActiveDateWeek)) {
      return 'Next week';
    }  else if (isOnSameDay(startOfLastWeek, startOfActiveDateWeek)) {
      return 'Last week';
    } else {
      return `w/c ${startOfActiveDateWeek?.format('Do MMMM')}`
    }
  }, [activeDate]);

  const onOpenMenuClicked = () => {
    dispatch(setSidebarOpen(true));
  }

  return (
    <>
      <Layout hideFooter={true} fullScreenView={true} style={{overflow: 'hidden'}}>
        <HeaderRowWrapper>
          <HeaderRowContent>
            <PageTitle text={"header.meeting-rooms"} overrideText={!!office ? office?.label + ' Office' : ' '} />
            <DateFlicker dateScale={DateScale.Week}
                         dateInTheMiddle={true}
                         showDate={true}
                         disabled={loading}
                         date={activeDate}
                         onChange={onDateChange}
                         hideDateText={false}
                         dateLabelOverride={dateLabel}
                         dateFormat={'dddd Do MMMM'}
                         updateGlobalDate={true} />
            <IconButton className={'hospitality__sideBarButton'} icon={IconTypes.Menu} onClick={onOpenMenuClicked} />
          </HeaderRowContent>
        </HeaderRowWrapper>
        <HospitalitySidebar />
        <Container isInTeams={isInTeams()}>
          <div className={'content'}>
            <ContentContainer>

              <MainContentView>
                {loading ? <>
                  <Center>
                    <LoadingSpinner fullScreen={false} hideBorder={true} />
                  </Center>
                </> : <>
                  {inProgressBookings.length > 0 || doneBookings.length > 0 ? <>
                    {inProgressBookings.map((value: HospitalityMeetingBookingSummary, key: number) => <HospitalityBookingSummary key={key} summary={value} />)}
                    {doneBookings.length > 0 && <BookingsListWrapper>
                      <HeadlineSmall style={{marginBottom: 16}}>{t('hospitality.done')}</HeadlineSmall>
                      {doneBookings.map((value: HospitalityMeetingBookingSummary, key: number) => <HospitalityBookingSummary key={key} summary={value} />)}
                    </BookingsListWrapper>}
                  </> : <NoHospitalityBookings />}
                </>}
              </MainContentView>

            </ContentContainer>
          </div>
        </Container>
      </Layout>
    </>
  )
}

interface Props {
}

const Container = styled.div<any>`
  height: ${props => props.isInTeams ? '100%' : 'calc(100vh - 232px)'};
  position: relative;
  display: flex;
  width: 100vw;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: ${Colours.veryLightGrey};
  .content {
  }
  
  .roomFinderView__header {
    max-width: ${breakpoints.desktopUberWide}px;
  }
  
  @media (${desktopSmall}) {
    height: ${props => props.isInTeams ? '100%' : 'calc(100vh - 198px)'};
    .roomFinderSidebar__closeBtn {
      display: none !important;
    }
    .content {
      padding-right: 400px;
    }
  }
  
  @media (${desktopWide}) {
    .content {
      padding-right: 400px;
    }
  }
  
  @media (${desktopMidWide}) {
    .content {
      padding-right: 300px;
    }
  }
  
  .roomFinderView__container {
    overflow: auto;
    height: calc(100vh - (${props => props.isInTeams ? '65px' : '114px'} + 175px));
    max-width: 100%;
    @media (${desktopSmall}) {
      height: calc(100vh - (${props => props.isInTeams ? '65px' : '114px'} + 72px));
    }
  }
  .roomFinderView__sidebar {
    position: absolute !important;
    height: 100%;
    width: 360px;
    right: 0;
    top: 0;
  }
`

const HeaderRowWrapper = styled.div`
  background-color: white;
  border-bottom: 1px solid var(--Neutrals-Light-Grey, #EDEDED);
`

const HeaderRowContent = styled.div`
  display: flex;
  align-items: center;
  max-width: 1400px;
  flex-direction: column;
  padding: 16px 0 0 16px;
  margin: 0 auto;
  position: relative;

  .pageTitle {
    margin: 0;
  }

  .hospitality__sideBarButton {
    position: absolute;
    right: 8px;
    top: 8px;
    i {
      font-size: 24px;
    }
  }
  .weekFlicker {
    padding: 0 16px 0 0;
  }
  @media (${desktopSmall}) {
    padding-right: 330px;
    flex-direction: row;
    .hospitality__sideBarButton {
      display: none;
    }
  }
  @media (${desktopUberWide}) {
    padding-right: 100px;
  }
`

const ContentContainer = styled.div`
  padding: 24px 0 60px 0;
`

const MainContentView = styled.div<any>`
  width: 100%;
  padding-bottom: 120px;
`

const BookingsListWrapper = styled.div<any>`
  max-width: 100%;
  width: 850px;
  margin: 24px auto 24px auto;
`
